/**
 * AdmissionsInquiry — public waitlist / inquiry form.
 *
 * Capacity-aware: shows rooms with current enrollment + spots left.
 * Submits to POST /api/admissions/inquiry. On success, shows a calm
 * "thank you" state.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import MarketingNav from "../components/MarketingNav";
import MarketingFooter from "../components/MarketingFooter";
import MetaTags from "../components/MetaTags";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function AdmissionsInquiry() {
  const { t } = useI18n();
  const [center, setCenter] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    child_name: "",
    child_dob: "",
    desired_start_date: "",
    desired_room_id: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, rRes] = await Promise.all([
          api.get("/admissions/center"),
          api.get("/admissions/center/rooms"),
        ]);
        setCenter(cRes.data);
        setRooms(rRes.data || []);
      } catch (e) {
        // ignore — form still renders without rooms
      }
    })();
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/admissions/inquiry", {
        ...form,
        organization_id: center?.organization_id,
        desired_room_id: form.desired_room_id || null,
      });
      setSubmitted(r.data);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = t("admissions.common.error_generic");
      if (typeof raw === "string" && raw) msg = raw;
      else if (Array.isArray(raw) && raw.length > 0) msg = raw[0]?.msg || msg;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admissions-inquiry-screen">
      <MetaTags
        title={t("admissions.inquiry.meta_title")}
        description={t("admissions.inquiry.meta_description")}
      />
      <MarketingNav />
      <main className="pt-32 sm:pt-40 pb-24">
        <section className="mx-auto max-w-7xl px-6 sm:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* LEFT: form */}
            <div>
              <span className="eyebrow">{t("admissions.inquiry.eyebrow")}</span>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
                {t("admissions.inquiry.headline_lead")}{" "}
                <em className="italic font-light text-[#1F3A2A]">
                  {t("admissions.inquiry.headline_italic")}
                </em>
              </h1>
              <p className="mt-5 text-lg text-[#6B7280] leading-relaxed max-w-md">
                {t("admissions.inquiry.subhead")}
              </p>

              {!submitted ? (
                <form
                  onSubmit={submit}
                  data-testid="admissions-inquiry-form"
                  className="mt-10 space-y-4"
                >
                  <Row>
                    <Field label={t("admissions.inquiry.field_parent_name")} required>
                      <input
                        data-testid="inquiry-parent-name"
                        type="text"
                        required
                        value={form.parent_name}
                        onChange={(e) => update("parent_name", e.target.value)}
                        className="input-soft w-full"
                      />
                    </Field>
                    <Field label={t("admissions.inquiry.field_parent_phone")}>
                      <input
                        data-testid="inquiry-parent-phone"
                        type="tel"
                        value={form.parent_phone}
                        onChange={(e) => update("parent_phone", e.target.value)}
                        className="input-soft w-full"
                      />
                    </Field>
                  </Row>
                  <Field label={t("admissions.inquiry.field_parent_email")} required>
                    <input
                      data-testid="inquiry-parent-email"
                      type="email"
                      required
                      value={form.parent_email}
                      onChange={(e) => update("parent_email", e.target.value)}
                      className="input-soft w-full"
                    />
                  </Field>
                  <Row>
                    <Field label={t("admissions.inquiry.field_child_name")} required>
                      <input
                        data-testid="inquiry-child-name"
                        type="text"
                        required
                        value={form.child_name}
                        onChange={(e) => update("child_name", e.target.value)}
                        className="input-soft w-full"
                      />
                    </Field>
                    <Field label={t("admissions.inquiry.field_child_dob")} required>
                      <input
                        data-testid="inquiry-child-dob"
                        type="date"
                        required
                        value={form.child_dob}
                        onChange={(e) => update("child_dob", e.target.value)}
                        className="input-soft w-full"
                      />
                    </Field>
                  </Row>
                  <Row>
                    <Field label={t("admissions.inquiry.field_desired_start")}>
                      <input
                        data-testid="inquiry-desired-start"
                        type="date"
                        value={form.desired_start_date}
                        onChange={(e) => update("desired_start_date", e.target.value)}
                        className="input-soft w-full"
                      />
                    </Field>
                    <Field label={t("admissions.inquiry.field_desired_room")}>
                      <select
                        data-testid="inquiry-desired-room"
                        value={form.desired_room_id}
                        onChange={(e) => update("desired_room_id", e.target.value)}
                        className="input-soft w-full"
                      >
                        <option value="">{t("admissions.inquiry.room_option_any")}</option>
                        {rooms.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                            {r.age_band ? ` · ${r.age_band}` : ""}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </Row>
                  <Field label={t("admissions.inquiry.field_notes")}>
                    <textarea
                      data-testid="inquiry-notes"
                      rows={3}
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      maxLength={1200}
                      className="input-soft w-full"
                    />
                  </Field>
                  <div className="pt-2">
                    <button
                      type="submit"
                      data-testid="inquiry-submit"
                      disabled={submitting}
                      className="btn-primary"
                    >
                      {submitting
                        ? t("admissions.inquiry.submit_pending")
                        : t("admissions.inquiry.submit")}
                    </button>
                  </div>
                </form>
              ) : (
                <div
                  data-testid="admissions-inquiry-thanks"
                  className="mt-10 rounded-3xl bg-[#1F3A2A] text-white px-7 py-9"
                >
                  <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#E6EFE8]">
                    {t("admissions.inquiry.thanks_eyebrow")}
                  </span>
                  <h2 className="mt-3 font-display text-2xl tracking-tight">
                    {t("admissions.inquiry.thanks_headline")}
                  </h2>
                  <p className="mt-4 text-white/85 leading-relaxed">
                    {t("admissions.inquiry.thanks_body")}
                  </p>
                  <Link
                    to="/"
                    className="mt-6 inline-block text-sm text-white underline underline-offset-4"
                  >
                    ← Back to home
                  </Link>
                </div>
              )}
            </div>

            {/* RIGHT: rooms with capacity */}
            <aside className="rounded-3xl bg-[#F7F8FC] border border-[#EEF0F4] p-7 self-start">
              <span className="eyebrow">{t("admissions.inquiry.rooms_eyebrow")}</span>
              <h2 className="mt-3 font-display text-xl text-[#1A1B2E]">
                {center?.name || "Maple Grove"}
              </h2>
              <ul className="mt-5 space-y-3" data-testid="inquiry-rooms-list">
                {rooms.length === 0 ? (
                  <li className="text-sm text-[#6B7280] italic">No rooms configured.</li>
                ) : (
                  rooms.map((r) => (
                    <li
                      key={r.id}
                      data-testid={`inquiry-room-${r.id}`}
                      className="rounded-xl bg-white border border-[#EEF0F4] p-4"
                    >
                      <div className="font-display text-lg text-[#1A1B2E]">{r.name}</div>
                      {r.age_band && (
                        <p className="text-xs text-[#6B7280]">{r.age_band}</p>
                      )}
                      <div className="mt-2 flex items-center justify-between text-xs">
                        {r.spots_available !== null && r.spots_available !== undefined ? (
                          <span
                            className={
                              r.spots_available === 0
                                ? "text-[#8C6D1F] font-semibold"
                                : "text-[#1F3A2A] font-semibold"
                            }
                          >
                            {t("admissions.inquiry.room_spots_left", {
                              n: r.spots_available,
                            })}
                          </span>
                        ) : (
                          <span className="text-[#6B7280]">{t("admissions.inquiry.room_no_capacity")}</span>
                        )}
                        {r.child_to_staff_ratio && (
                          <span className="text-[#6B7280]">
                            {t("admissions.inquiry.room_ratio", { ratio: r.child_to_staff_ratio })}
                          </span>
                        )}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </aside>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
        {label}
        {required && <span className="text-[#FF6B6B] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
